import { db } from './database';
import { auth } from './auth';
import { User, Product, CartItem, Order, SocialLink, ProfileAnalytics, AnalyticsEvent, ProductAnalytics } from '../types';

class ApiService {
  async init() {
    await db.init();
  }

  // User Management
  async createUserProfile(userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User> {
    const user: User = {
      ...userData,
      id: auth.getCurrentUser()?.id || `user-${Date.now()}`,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await db.put('users', user);
    return user;
  }

  async updateUserProfile(userId: string, updates: Partial<User>): Promise<User> {
    const existingUser = await db.get<User>('users', userId);
    if (!existingUser) throw new Error('User not found');

    const updatedUser: User = {
      ...existingUser,
      ...updates,
      updatedAt: new Date()
    };

    await db.put('users', updatedUser);
    return updatedUser;
  }

  async getUserByUsername(username: string): Promise<User | null> {
    return await db.getByIndex<User>('users', 'username', username);
  }

  async getUserById(userId: string): Promise<User | null> {
    return await db.get<User>('users', userId);
  }

  // Product Management
  async createProduct(productData: Omit<Product, 'id' | 'productNumber' | 'analytics' | 'createdAt' | 'updatedAt'>): Promise<Product> {
    const productNumber = await this.getNextProductNumber(productData.userId);
    
    const product: Product = {
      ...productData,
      id: `product-${Date.now()}`,
      productNumber,
      analytics: {
        views: 0,
        clicks: 0
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await db.put('products', product);
    return product;
  }

  private async getNextProductNumber(userId: string): Promise<number> {
    const key = `product_counter_${userId}`;
    const counter = await db.get<{ key: string; value: number }>('metadata', key);
    const nextNumber = (counter?.value || 0) + 1;
    
    await db.put('metadata', { key, value: nextNumber });
    return nextNumber;
  }

  async updateProduct(productId: string, updates: Partial<Product>): Promise<Product> {
    const existingProduct = await db.get<Product>('products', productId);
    if (!existingProduct) throw new Error('Product not found');

    const updatedProduct: Product = {
      ...existingProduct,
      ...updates,
      updatedAt: new Date()
    };

    await db.put('products', updatedProduct);
    return updatedProduct;
  }

  async deleteProduct(productId: string): Promise<void> {
    await db.delete('products', productId);
  }

  async getProductsByUserId(userId: string): Promise<Product[]> {
    return await db.getAllByIndex<Product>('products', 'userId', userId);
  }

  async getProduct(productId: string): Promise<Product | null> {
    return await db.get<Product>('products', productId);
  }

  // Cart Management
  async getCart(): Promise<CartItem[]> {
    return await db.getAll<CartItem>('cart');
  }

  async addToCart(productId: string): Promise<void> {
    const product = await this.getProduct(productId);
    if (!product) throw new Error('Product not found');

    const cart = await this.getCart();
    const existingItem = cart.find(item => item.productId === productId);

    if (existingItem) {
      existingItem.quantity += 1;
      await db.put('cart', existingItem);
    } else {
      const newItem: CartItem = {
        id: `cart-${Date.now()}`,
        productId,
        product,
        quantity: 1,
        addedAt: new Date()
      };
      await db.put('cart', newItem);
    }
  }

  async updateCartItemQuantity(cartItemId: string, quantity: number): Promise<void> {
    if (quantity <= 0) {
      await db.delete('cart', cartItemId);
    } else {
      const item = await db.get<CartItem>('cart', cartItemId);
      if (item) {
        item.quantity = quantity;
        await db.put('cart', item);
      }
    }
  }

  async clearCart(): Promise<void> {
    const cart = await this.getCart();
    for (const item of cart) {
      await db.delete('cart', item.id);
    }
  }

  // Order Management
  async createOrder(customerData: {
    email?: string;
    name?: string;
    phone?: string;
  }): Promise<Order> {
    const cart = await this.getCart();
    if (cart.length === 0) throw new Error('Cart is empty');

    const total = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);

    const order: Order = {
      id: `order-${Date.now()}`,
      userId: cart[0].product.userId,
      items: cart,
      total,
      customerEmail: customerData.email,
      customerName: customerData.name,
      customerPhone: customerData.phone,
      status: 'pending',
      createdAt: new Date()
    };

    await db.put('orders', order);
    await this.clearCart();

    return order;
  }

  async getOrdersByUserId(userId: string): Promise<Order[]> {
    return await db.getAllByIndex<Order>('orders', 'userId', userId);
  }

  async updateOrderStatus(orderId: string, status: Order['status']): Promise<void> {
    const order = await db.get<Order>('orders', orderId);
    if (order) {
      order.status = status;
      await db.put('orders', order);
    }
  }

  // Analytics Management
  async trackEvent(userId: string, type: AnalyticsEvent['type'], targetId?: string, metadata?: Record<string, any>): Promise<void> {
    const event: AnalyticsEvent = {
      id: `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      userId,
      type,
      targetId,
      metadata,
      timestamp: new Date()
    };

    await db.put('events', event);

    // Update relevant analytics
    switch (type) {
      case 'profile_view':
        await this.updateProfileAnalytics(userId, 'view');
        break;
      case 'product_view':
        if (targetId) {
          await this.updateProductAnalytics(targetId, 'view');
          await this.updateProfileAnalytics(userId, 'product_view', targetId);
        }
        break;
      case 'product_click':
        if (targetId) {
          await this.updateProductAnalytics(targetId, 'click');
          await this.updateProfileAnalytics(userId, 'product_click', targetId);
        }
        break;
      case 'social_link_click':
        if (targetId) {
          await this.updateProfileAnalytics(userId, 'social_click', targetId);
        }
        break;
    }
  }

  private async updateProductAnalytics(productId: string, action: 'view' | 'click'): Promise<void> {
    const product = await db.get<Product>('products', productId);
    if (product) {
      if (action === 'view') {
        product.analytics.views++;
        product.analytics.lastViewedAt = new Date();
      } else if (action === 'click') {
        product.analytics.clicks++;
        product.analytics.lastClickedAt = new Date();
      }
      await db.put('products', product);
    }
  }

  private async updateProfileAnalytics(userId: string, action: 'view' | 'product_view' | 'product_click' | 'social_click', targetId?: string): Promise<void> {
    let analytics = await db.get<ProfileAnalytics>('analytics', `profile-${userId}`);
    
    if (!analytics) {
      analytics = {
        id: `profile-${userId}`,
        userId,
        totalViews: 0,
        totalClicks: 0,
        dailyViews: [],
        productClicks: {},
        socialLinkClicks: {},
        createdAt: new Date(),
        updatedAt: new Date()
      };
    }

    const today = new Date().toISOString().split('T')[0];
    let todayAnalytics = analytics.dailyViews.find(d => d.date === today);
    
    if (!todayAnalytics) {
      todayAnalytics = { date: today, views: 0, clicks: 0 };
      analytics.dailyViews.push(todayAnalytics);
    }

    switch (action) {
      case 'view':
        analytics.totalViews++;
        todayAnalytics.views++;
        break;
      case 'product_view':
        // Views are tracked at profile level, not per product
        break;
      case 'product_click':
        analytics.totalClicks++;
        todayAnalytics.clicks++;
        if (targetId) {
          analytics.productClicks[targetId] = (analytics.productClicks[targetId] || 0) + 1;
        }
        break;
      case 'social_click':
        analytics.totalClicks++;
        todayAnalytics.clicks++;
        if (targetId) {
          analytics.socialLinkClicks[targetId] = (analytics.socialLinkClicks[targetId] || 0) + 1;
        }
        break;
    }

    analytics.updatedAt = new Date();
    
    // Keep only last 30 days of daily analytics
    analytics.dailyViews = analytics.dailyViews
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 30);

    await db.put('analytics', analytics);
  }

  async getProfileAnalytics(userId: string): Promise<ProfileAnalytics | null> {
    return await db.get<ProfileAnalytics>('analytics', `profile-${userId}`);
  }

  async getAnalyticsEvents(userId: string, limit?: number): Promise<AnalyticsEvent[]> {
    const allEvents = await db.getAllByIndex<AnalyticsEvent>('events', 'userId', userId);
    const sortedEvents = allEvents.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    return limit ? sortedEvents.slice(0, limit) : sortedEvents;
  }
}

export const api = new ApiService();